import { useFocusEffect, useScrollToTop } from '@react-navigation/native';
import { setBadgeCountAsync } from 'expo-notifications';
import type { FC } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, View } from 'react-native';
import { Badge, Chip, Text } from 'react-native-paper';

import {
  ArrowOutwardIcon,
  ArticlePersonIcon,
  BrushIcon,
  ChatsIcon,
  DeleteForeverIcon,
  DraftsIcon,
  EditIcon,
  ErrorIcon,
  FamilyHomeIcon,
  FavoriteIcon,
  GavelIcon,
  KeyIcon,
  LogoutIcon,
  MenuBookIcon,
  MyLocationIcon,
  NotificationsIcon,
  RecordVoiceOverIcon,
  SupportIcon
} from '@/assets';
import { APP_VERSION } from '@/configs/version';
import { APP_NAME } from '@/constants/app';
import { useGetOwnGlobalRoleAssignments } from '@/features/api/hooks/participant/useGetOwnGlobalRoleAssignments';
import { useGetPerson } from '@/features/api/hooks/participant/useGetPerson';
import { useCollectUserDataAndSendMail } from '@/features/api/hooks/shared-data-privacy/useCollectUserDataAndSendMail';
import { useRevokeAllRefreshTokens } from '@/features/api/hooks/shared-oauth/useRevokeAllRefreshTokens';
import { useAuth } from '@/features/authorization/provider/useAuth';
import { getTokenExpireTimeInMinutes } from '@/features/authorization/utils/token';
import { useFeature } from '@/features/feature-management/provider/useFeature';
import { useFeatureSettingStore } from '@/features/feature-management/store/featureSettingStore';
import { FeaturesEnum } from '@/features/feature-management/types/features';
import { MeineRegionRadiusModal } from '@/features/geo-area/components/MeineRegionRadiusModal';
import type { MeineRegionRadiusModalHandle } from '@/features/geo-area/components/MeineRegionRadiusModal';
import { HomeAreaChangeSheet } from '@/features/geo-area/components/subscription/HomeAreaChangeSheet';
import { useRegionalfunkRadiusStore } from '@/features/geo-area/store/useRegionalfunkRadiusStore';
import { ListItem } from '@/features/list/components/ListItem';
import { ListSection } from '@/features/list/components/ListSection';
import { PersonAvatar } from '@/features/media/components/avatar/PersonAvatar';
import { usePopup } from '@/features/popup/provider/usePopup';
import { OrganizationListSection } from '@/features/profile-setting/components/OrganizationListSection';
import {
  INVITE_URL,
  HELP_URL
} from '@/features/profile-setting/constants/links';
import { useTutorial } from '@/features/tutorial/providers/useTutorial';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { NavigationButton } from '@/navigation/components/NavigationButton';
import { NavigationListItem } from '@/navigation/components/NavigationListItem';
import { AppRoute } from '@/navigation/types/navigation';
import { GradientBackground } from '@/shared/components/GradientBackground';
import { StateView } from '@/shared/components/state-view/StateView';
import { BadgeKey, useBadgeStore } from '@/shared/store/useBadgeStore';
import { share } from '@/shared/utils/share/share';
import { getPublicName } from '@/shared/utils/string';
import { handleOpenUrl } from '@/shared/utils/url';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

const makeStyles = () =>
  StyleSheet.create({
    gradient: { flex: 1 },
    pageContent: { paddingBottom: 32 },
    headerWrapper: {
      marginTop: 0,
      gap: 16,
      paddingTop: 48,
      paddingHorizontal: 32,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: 'transparent'
    },
    name: {
      textAlign: 'center',
      justifyContent: 'center',
      alignItems: 'center'
    },
    roles: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: 8,
      paddingTop: 8
    },
    version: { textAlign: 'center', padding: 16 }
  });

export const ProfileSettingScreen: FC = () => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(), []);
  const { present: presentTutorial } = useTutorial();

  const { t } = useTranslation(LocalizationNameSpace.PROFILE_SETTING, {
    keyPrefix: 'screens.profile-setting'
  });

  const { mutateAsync: revokeAllRefreshTokens } = useRevokeAllRefreshTokens();

  const { logout, changePassword } = useAuth();
  const { data: person, isLoading, refetch, isRefetching } = useGetPerson();
  const { data: roleAssignments } = useGetOwnGlobalRoleAssignments();

  const { showAlert, showToast } = usePopup();
  const { mutate: collectUserDataAndSendMail } =
    useCollectUserDataAndSendMail();

  const handleRequestData = useCallback(
    () =>
      showAlert({
        dismissable: true,
        labels: t('alerts.request-data', {
          returnObjects: true,
          email: person?.email
        }),
        onConfirm: () => {
          collectUserDataAndSendMail();
        }
      }),
    [collectUserDataAndSendMail, person?.email, showAlert, t]
  );

  const handleInvite = useCallback(async () => {
    await share({
      message: [
        t('share.title', { appName: APP_NAME }),
        t('share.message', { appName: APP_NAME })
      ].join('\n\n'),
      url: INVITE_URL,
      note: t('share.note')
    });
  }, [t]);

  const unreadMessageCount = Number(
    useBadgeStore((state) => state.badges)[BadgeKey.Chat]?.value
  );

  const handleGlobalLogout = useCallback(async () => {
    await revokeAllRefreshTokens();
    logout();
  }, [logout, revokeAllRefreshTokens]);

  const { isFeatureEnabled } = useFeature();

  const setFeature = useFeatureSettingStore((state) => state.setFeature);

  const [versionPressedCount, setVersionPressedCount] = useState(0);

  const handleVersionPressed = useCallback(() => {
    if (!__DEV__) return;

    const newCount = versionPressedCount + 1;
    setVersionPressedCount(newCount);
    if (newCount >= 3) {
      setFeature(FeaturesEnum.DevMode, true);
      showToast({ message: 'Dev mode enabled' });
    }
  }, [setFeature, showToast, versionPressedCount]);

  const radiusModalRef = useRef<MeineRegionRadiusModalHandle>(null);
  const [homeAreaSheetVisible, setHomeAreaSheetVisible] = useState(false);
  const meineRegionRadiusKm = useRegionalfunkRadiusStore(
    (s) => s.meineRegionRadiusKm
  );

  const viewRef = useRef(null);
  useScrollToTop(viewRef);

  // reset app badge
  useFocusEffect(
    useCallback(() => {
      setBadgeCountAsync(0);
    }, [])
  );

  return (
    <GradientBackground>
      <StateView
        ref={viewRef}
        onRefetch={refetch}
        isLoading={isLoading}
        isRefetching={isRefetching}
        stateLabels={t('state', { returnObjects: true })}
        backgroundColor="transparent"
        contentContainerStyle={styles.pageContent}
      >
        <ListSection
          style={styles.headerWrapper}
          headerBackgroundColor="transparent"
        >
          {person && <PersonAvatar person={person} size={176} canOpenGallery />}
          <Text variant="headlineMedium" style={styles.name}>
            {person?.firstName} {person?.lastName}
          </Text>
          <View style={styles.name}>
            <Text variant="labelMedium">{t('public')}</Text>
            <Text variant="titleMedium">
              {getPublicName(person?.firstName, person?.lastName)}
            </Text>
          </View>
          {roleAssignments && roleAssignments.length > 0 && (
            <View style={styles.roles}>
              {roleAssignments.map((assignment) => (
                <Chip key={assignment.role} compact>
                  {assignment.role}
                </Chip>
              ))}
            </View>
          )}
          <NavigationButton
            screen={AppRoute.ProfileEdit}
            icon={() => (
              <EditIcon fill={theme.colors.surface} width={18} height={18} />
            )}
            mode="contained"
            buttonColor={theme.colors.primary}
          >
            {t('edit')}
          </NavigationButton>
        </ListSection>
        <ListSection
          title={t('contact')}
          style={{ backgroundColor: 'transparent' }}
          headerBackgroundColor="transparent"
        >
          <NavigationListItem
            backgroundColor={theme.colors.surfaceContainerLowest}
            screen={AppRoute.Chats}
            title={t(AppRoute.Chats)}
            badge={
              unreadMessageCount > 0 && (
                <Badge style={{ backgroundColor: theme.colors.error }}>
                  {unreadMessageCount}
                </Badge>
              )
            }
            icon={ChatsIcon}
          />
          <NavigationListItem
            backgroundColor={theme.colors.surfaceContainerLowest}
            screen={AppRoute.MyPosts}
            title={t(AppRoute.MyPosts)}
            icon={RecordVoiceOverIcon}
          />
          <NavigationListItem
            backgroundColor={theme.colors.surfaceContainerLowest}
            screen={AppRoute.Participated}
            title={t(AppRoute.Participated)}
            icon={FavoriteIcon}
          />
        </ListSection>
        <OrganizationListSection />
        <ListSection
          title={t('settings')}
          style={{ backgroundColor: 'transparent' }}
          headerBackgroundColor="transparent"
        >
          <NavigationListItem
            backgroundColor={theme.colors.surfaceContainerLowest}
            screen={AppRoute.NotificationSetting}
            title={t(AppRoute.NotificationSetting)}
            icon={NotificationsIcon}
          />
          <ListItem
            backgroundColor={theme.colors.surfaceContainerLowest}
            title={t('geo-area-subscription')}
            onPress={() => setHomeAreaSheetVisible(true)}
            icon={FamilyHomeIcon}
          />
          <ListItem
            backgroundColor={theme.colors.surfaceContainerLowest}
            title={t('myRadius')}
            description={t('myRadiusDescription', { km: meineRegionRadiusKm })}
            textOrder="column-reverse"
            onPress={() => radiusModalRef.current?.open()}
            icon={MyLocationIcon}
          />
          <NavigationListItem
            backgroundColor={theme.colors.surfaceContainerLowest}
            screen={AppRoute.AppSetting}
            title={t(AppRoute.AppSetting)}
            icon={BrushIcon}
          />
          {isFeatureEnabled(FeaturesEnum.DevMode) && (
            <NavigationListItem
              backgroundColor={theme.colors.surfaceContainerLowest}
              screen={AppRoute.Experiments}
              title={t(AppRoute.Experiments)}
              icon={ErrorIcon}
            />
          )}
        </ListSection>
        <ListSection
          title={t('more')}
          style={{ backgroundColor: 'transparent' }}
          headerBackgroundColor="transparent"
        >
          <ListItem
            backgroundColor={theme.colors.surfaceContainerLowest}
            title={t(AppRoute.Help)}
            onPress={() => {
              handleOpenUrl(HELP_URL);
            }}
            icon={SupportIcon}
            arrowIcon={ArrowOutwardIcon}
          />
          <ListItem
            backgroundColor={theme.colors.surfaceContainerLowest}
            title={t('first-steps')}
            onPress={presentTutorial}
            icon={MenuBookIcon}
          />
          <ListItem
            backgroundColor={theme.colors.surfaceContainerLowest}
            title={t('invite-friend')}
            onPress={() => {
              handleInvite();
            }}
            icon={DraftsIcon}
          />
          <NavigationListItem
            backgroundColor={theme.colors.surfaceContainerLowest}
            screen={AppRoute.Legal}
            title={t(AppRoute.Legal)}
            icon={GavelIcon}
          />
        </ListSection>
        <ListSection
          title={t('account')}
          style={{ backgroundColor: 'transparent' }}
          headerBackgroundColor="transparent"
        >
          <ListItem
            backgroundColor={theme.colors.surfaceContainerLowest}
            title={t('change-password')}
            onPress={changePassword}
            icon={KeyIcon}
          />
          <ListItem
            backgroundColor={theme.colors.surfaceContainerLowest}
            onPress={handleRequestData}
            title={t('request-data')}
            icon={ArticlePersonIcon}
          />
          <NavigationListItem
            backgroundColor={theme.colors.surfaceContainerLowest}
            screen={AppRoute.DeleteAccount}
            title={t(AppRoute.DeleteAccount)}
            icon={DeleteForeverIcon}
          />
          <ListItem
            textOrder="column-reverse"
            backgroundColor={theme.colors.surfaceContainerLowest}
            title={t('globalLogout')}
            // Todo: update label and / or functionality
            description={t('globalLogoutIn', {
              duration: getTokenExpireTimeInMinutes()
            })}
            numberOfLinesDescription={3}
            onPress={() => {
              handleGlobalLogout();
            }}
            icon={LogoutIcon}
          />
          <ListItem
            backgroundColor={theme.colors.surfaceContainerLowest}
            title={t('logout')}
            onPress={logout}
            icon={LogoutIcon}
          />
        </ListSection>
        <Pressable accessibilityRole="button" onPress={handleVersionPressed}>
          <Text style={styles.version}>
            {t('version', {
              version: APP_VERSION ?? t('unknown')
            })}
          </Text>
        </Pressable>
      </StateView>
      <MeineRegionRadiusModal ref={radiusModalRef} />
      <HomeAreaChangeSheet
        visible={homeAreaSheetVisible}
        onDismiss={() => setHomeAreaSheetVisible(false)}
      />
    </GradientBackground>
  );
};
